import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Bell,
  Settings,
  Edit3,
  Calendar,
  Wallet,
  ShieldCheck,
  FileText,
  MapPin,
  Gauge,
  Fuel,
  Users,
  Cog,
  Star,
  Loader2,
  Car,
  Wrench,
  EyeOff,
  CheckCircle2,
  ImagePlus,
  Upload,
  Trash2,
  X
} from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { ownerService } from '../services/owner.service';
import { getDefaultDailyPrice, normalizePricingConfigurations, PRICING_TIER_OPTIONS } from '../utils/pricing';
import { showAppAlert } from '../utils/appAlert';
import { buildPublicFileUrl } from '../utils/fileUrl';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=1200';

const buildImageUrl = (imageUrl) => {
  if (!imageUrl) return FALLBACK_IMAGE;
  return buildPublicFileUrl(imageUrl);
};

const formatMoney = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'RF 0';
  }
  return `RF ${Math.round(Number(value)).toLocaleString()}`;
};

const VehicleDetailsOwner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageDeletingId, setImageDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [pricingForm, setPricingForm] = useState({
    outsideCityPrice: '',
    pricingConfigurations: []
  });

  const fetchCar = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await ownerService.getOwnerCarDetails(id);
      setCar(data);
    } catch (err) {
      setError(err.message || 'Failed to load car details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCar();
  }, [fetchCar]);

  const gallery = useMemo(() => {
    if (!car) return [];
    const extras = Array.isArray(car.images) ? car.images : [];
    const ordered = car.primaryImageUrl
      ? [{ id: 'primary', imageUrl: car.primaryImageUrl }, ...extras]
      : extras;
    return ordered.map((image) => ({
      ...image,
      src: buildImageUrl(image.imageUrl)
    }));
  }, [car]);

  const profileName = [localStorage.getItem('firstName'), localStorage.getItem('lastName')]
    .filter(Boolean)
    .join(' ')
    .trim() || 'Owner';

  const statusColor = {
    AVAILABLE: 'bg-[#DCFCE7] text-[#166534]',
    APPROVED: 'bg-[#FFEDD5] text-[#FF6B00]',
    PENDING_APPROVAL: 'bg-amber-50 text-amber-700',
    REJECTED: 'bg-red-50 text-red-700',
    MAINTENANCE: 'bg-orange-50 text-orange-700',
    BOOKED: 'bg-brand-orange-50 text-brand-black',
    IN_USE: 'bg-brand-orange-50 text-brand-black',
    DRAFT: 'bg-gray-100 text-gray-700',
    SUSPENDED: 'bg-gray-200 text-gray-700'
  };
  const formatStatusLabel = (status) => {
    if (!status) return 'UNKNOWN';
    return status.replace(/_/g, ' ');
  };

  const canEdit = car?.status === 'DRAFT' || car?.status === 'REJECTED';
  const canManageAvailability = car && !['DRAFT', 'REJECTED', 'PENDING_APPROVAL', 'BOOKED', 'IN_USE'].includes(car.status);
  const canManagePricing = car && !['PENDING_APPROVAL', 'BOOKED', 'IN_USE'].includes(car.status);
  const canManageImages = car && !['PENDING_APPROVAL', 'BOOKED', 'IN_USE'].includes(car.status);
  const pricingConfigurations = normalizePricingConfigurations(car?.pricingConfigurations || []);

  useEffect(() => {
    if (!car) return;

    const currentPricingMap = new Map((car.pricingConfigurations || []).map((item) => [item.pricingType, item.amount]));
    setPricingForm({
      outsideCityPrice: car.outsideCityPrice ?? '',
      pricingConfigurations: PRICING_TIER_OPTIONS.map((option) => ({
        pricingType: option.pricingType,
        amount: currentPricingMap.get(option.pricingType) ?? ''
      }))
    });
  }, [car]);

  const quickStats = [
    { label: 'Daily Price', value: formatMoney(getDefaultDailyPrice(pricingConfigurations, car?.pricePerDay)), icon: Wallet },
    { label: 'Outside City', value: formatMoney(car?.outsideCityPrice), icon: MapPin },
    { label: 'Transmission', value: car?.transmission || 'Not set', icon: Cog },
    { label: 'Seats', value: car?.seats || 'Not set', icon: Users }
  ];

  const availabilityOptions = [
    {
      value: 'AVAILABLE',
      label: 'Available',
      description: 'Show the vehicle on the platform and allow new bookings.',
      icon: CheckCircle2
    },
    {
      value: 'MAINTENANCE',
      label: 'Maintenance',
      description: 'Temporarily hide bookings while the car is under service.',
      icon: Wrench
    },
    {
      value: 'SUSPENDED',
      label: 'Suspended',
      description: 'Hide the vehicle from active platform listing until you make it available again.',
      icon: EyeOff
    }
  ];

  const handleStatusChange = async (nextStatus) => {
    try {
      setStatusSaving(true);
      await ownerService.updateFleetStatus(car.id, { status: nextStatus });
      setAvailabilityOpen(false);
      await fetchCar();
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Availability Update Failed', message: err.message || 'Failed to update availability' });
    } finally {
      setStatusSaving(false);
    }
  };

  const handlePricingValueChange = (pricingType, amount) => {
    setPricingForm((current) => ({
      ...current,
      pricingConfigurations: current.pricingConfigurations.map((item) => (
        item.pricingType === pricingType ? { ...item, amount } : item
      ))
    }));
  };

  const handlePricingSave = async () => {
    if (!pricingForm.outsideCityPrice || Number(pricingForm.outsideCityPrice) <= 0) {
      showAppAlert({ type: 'error', title: 'Invalid Price', message: 'Outside city price must be greater than zero.' });
      return;
    }

    const missingTier = pricingForm.pricingConfigurations.find((item) => !item.amount || Number(item.amount) <= 0);
    if (missingTier) {
      const tierLabel = PRICING_TIER_OPTIONS.find((option) => option.pricingType === missingTier.pricingType)?.label || 'Pricing tier';
      showAppAlert({ type: 'error', title: 'Missing Pricing', message: `${tierLabel} amount must be greater than zero.` });
      return;
    }

    try {
      setPricingSaving(true);
      await ownerService.updateFleetPricing(car.id, {
        outsideCityPrice: Number(pricingForm.outsideCityPrice),
        pricingConfigurations: pricingForm.pricingConfigurations.map((item) => ({
          pricingType: item.pricingType,
          amount: Number(item.amount)
        }))
      });
      setPricingOpen(false);
      await fetchCar();
      showAppAlert({ type: 'success', title: 'Pricing Updated', message: 'Your vehicle pricing has been updated successfully.' });
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Pricing Update Failed', message: err.message || 'Failed to update pricing.' });
    } finally {
      setPricingSaving(false);
    }
  };

  const handlePrimaryImageUpload = async (file) => {
    if (!file) return;

    try {
      setImageUploading(true);
      await ownerService.uploadPrimaryImage(car.id, file);
      await fetchCar();
      showAppAlert({ type: 'success', title: 'Primary Image Updated', message: 'Your primary vehicle image has been updated.' });
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Upload Failed', message: err.message || 'Failed to upload primary image.' });
    } finally {
      setImageUploading(false);
    }
  };

  const handleGalleryImagesUpload = async (files) => {
    if (!files || !files.length) return;

    try {
      setImageUploading(true);
      await ownerService.uploadImages(car.id, files);
      await fetchCar();
      showAppAlert({ type: 'success', title: 'Images Uploaded', message: 'Your gallery images have been updated.' });
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Upload Failed', message: err.message || 'Failed to upload gallery images.' });
    } finally {
      setImageUploading(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!imageId) return;

    try {
      setImageDeletingId(imageId);
      await ownerService.deleteImage(car.id, imageId);
      await fetchCar();
      showAppAlert({ type: 'success', title: 'Image Removed', message: 'The gallery image has been removed.' });
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Delete Failed', message: err.message || 'Failed to remove image.' });
    } finally {
      setImageDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[#F8F9FA]">
        <Sidebar activePage="Fleet" />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </main>
      </div>
    );
  }

  if (error || !car) {
    return (
      <div className="flex h-screen bg-[#F8F9FA]">
        <Sidebar activePage="Fleet" />
        <main className="flex-grow flex items-center justify-center p-10">
          <div className="max-w-md w-full bg-white border border-gray-100 rounded-3xl p-10 text-center shadow-sm">
            <h1 className="text-2xl font-black text-brand-black mb-3">Car details unavailable</h1>
            <p className="text-sm text-gray-500 mb-8">{error || 'We could not load this vehicle right now.'}</p>
            <button
              onClick={() => navigate('/fleet')}
              className="px-6 py-3 bg-brand-black text-white rounded-2xl text-xs font-black uppercase tracking-widest"
            >
              Back to Fleet
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden">
      <Sidebar activePage="Fleet" />

      <main className="flex-grow overflow-y-auto">
        <header className="bg-white border-b border-gray-100 flex items-center justify-between px-8 py-4 sticky top-0 z-20">
          <button
            onClick={() => navigate('/fleet')}
            className="flex items-center gap-2 text-gray-400 hover:text-brand-black transition-all font-bold text-sm"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Fleet
          </button>

          <div className="flex items-center gap-5">
            <button className="p-2.5 text-gray-400 bg-gray-50 rounded-xl hover:text-brand-black transition-all relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
            <div className="flex items-center gap-3 pl-5 border-l border-gray-100">
              <span className="text-sm font-black text-gray-900">{profileName}</span>
              <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center">
                <Settings className="w-5 h-5" />
              </div>
            </div>
          </div>
        </header>

        <div className="p-8 max-w-7xl mx-auto space-y-6">
          <section>
            <div className="inline-flex gap-2 p-1.5 rounded-[20px] bg-gray-100">
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className={`px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === 'details' ? 'bg-white text-brand-black shadow-sm' : 'text-gray-500'
                }`}
              >
                Car Details
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={`px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === 'history' ? 'bg-white text-brand-black shadow-sm' : 'text-gray-500'
                }`}
              >
                History
              </button>
            </div>
          </section>

          {activeTab === 'details' && (
            <>
          <section className="bg-white border border-gray-100 rounded-[28px] p-6 shadow-sm">
            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
              <div>
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <h1 className="text-3xl md:text-4xl font-black tracking-tight">{car.name || 'Vehicle'}</h1>
                <span className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${statusColor[car.status] || 'bg-gray-100 text-gray-700'}`}>
                  {formatStatusLabel(car.status)}
                </span>
                </div>
                <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">
                  Plate: {car.plateNumber || 'N/A'} {car.lastUpdated ? `• Last updated: ${car.lastUpdated}` : ''}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {canEdit && (
                  <button
                    onClick={() => navigate(`/add-vehicle?id=${car.id}`)}
                    className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all"
                  >
                    <Edit3 className="w-4 h-4 text-gray-400" />
                    Edit Details
                  </button>
                )}
                {canManageAvailability && (
                  <button
                    onClick={() => setAvailabilityOpen(true)}
                    className="flex items-center gap-2 px-5 py-3 bg-brand-black text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-800 transition-all"
                  >
                    <Calendar className="w-4 h-4" />
                    Manage Availability
                  </button>
                )}
                {canManagePricing && (
                  <button
                    onClick={() => setPricingOpen(true)}
                    className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all"
                  >
                    <Wallet className="w-4 h-4 text-gray-500" />
                    Update Pricing
                  </button>
                )}
                {canManageImages && (
                  <button
                    onClick={() => setImagesOpen(true)}
                    className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all"
                  >
                    <ImagePlus className="w-4 h-4 text-gray-500" />
                    Manage Images
                  </button>
                )}
              </div>
            </div>
          </section>

          {car.status === 'REJECTED' && car.reviewComment && (
            <section className="rounded-[28px] border border-red-100 bg-red-50 p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-2">Changes required before resubmitting</p>
              <p className="text-sm font-bold text-red-800">{car.reviewComment}</p>
              <div className="mt-4">
                <button
                  onClick={() => navigate(`/add-vehicle?id=${car.id}`)}
                  className="px-5 py-3 bg-red-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-red-700"
                >
                  Update Car And Resubmit
                </button>
              </div>
            </section>
          )}

          <section className="grid grid-cols-1 xl:grid-cols-[1.4fr_0.8fr] gap-6">
            <div className="bg-white border border-gray-100 rounded-[28px] p-4 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-[1.45fr_0.85fr] gap-4">
                <div className="rounded-[24px] overflow-hidden bg-gray-100 min-h-[360px]">
                  <img src={gallery[0]?.src || FALLBACK_IMAGE} alt={car.name} className="w-full h-full object-cover" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                  {gallery.slice(1, 5).map((image, index) => (
                    <div key={image.id || index} className="rounded-[22px] overflow-hidden bg-gray-100 h-[172px]">
                      <img src={image.src} alt={`${car.name} ${index + 2}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {!gallery.slice(1, 5).length && (
                    <div className="rounded-[22px] overflow-hidden bg-gray-100 h-[172px] flex items-center justify-center">
                      <Car className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {quickStats.map((item) => (
                <div key={item.label} className="bg-white border border-gray-100 rounded-[24px] p-5 shadow-sm">
                  <div className="w-11 h-11 rounded-2xl bg-gray-50 text-brand-black flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{item.label}</p>
                  <p className="text-lg font-black text-brand-black leading-tight">{item.value}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-[1fr_0.9fr] gap-6">
            <div className="space-y-6">
              <div className="bg-white rounded-[28px] border border-gray-100 p-6 shadow-sm">
                <h2 className="text-xl font-black tracking-tight uppercase mb-5">Vehicle Specs</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: MapPin, label: 'City', value: car.cityName || 'Not set' },
                    { icon: Gauge, label: 'Odometer', value: car.odometer ? `${car.odometer} km` : 'Not set' },
                    { icon: Fuel, label: 'Fuel Type', value: car.fuelType || 'Not set' },
                    { icon: Cog, label: 'Transmission', value: car.transmission || 'Not set' },
                    { icon: Users, label: 'Seats', value: car.seats || 'Not set' },
                    { icon: Car, label: 'Year', value: car.year || 'Not set' }
                  ].map(item => (
                    <div key={item.label} className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 flex items-center gap-4">
                      <div className="w-11 h-11 rounded-2xl bg-white text-brand-black flex items-center justify-center shadow-sm">
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{item.label}</p>
                        <p className="text-sm font-black text-brand-black mt-1">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">Specifications</p>
                  <div className="flex flex-wrap gap-3">
                    {car.specifications?.length ? car.specifications.map(spec => (
                      <span key={spec} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-[10px] font-black uppercase tracking-widest">
                        {spec}
                      </span>
                    )) : (
                      <span className="text-sm text-gray-500">No specifications added yet.</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[28px] border border-gray-100 p-6 shadow-sm">
                <h2 className="text-xl font-black tracking-tight uppercase mb-5">Pricing & Rules</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  <div className="rounded-2xl bg-gray-50/60 border border-gray-100 p-5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Default Daily Price</p>
                    <div className="text-2xl font-black text-brand-black">{formatMoney(getDefaultDailyPrice(pricingConfigurations, car.pricePerDay))}</div>
                  </div>
                  <div className="rounded-2xl bg-gray-50/60 border border-gray-100 p-5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Outside City</p>
                    <div className="text-2xl font-black text-brand-black">{formatMoney(car.outsideCityPrice)}</div>
                  </div>
                </div>
                <div className="space-y-3 mb-5">
                  {pricingConfigurations.length ? pricingConfigurations.map((item) => (
                    <div key={item.pricingType} className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-4">
                      <div>
                        <p className="text-sm font-black text-brand-black">{item.label}</p>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">{item.rangeLabel}</p>
                      </div>
                      <span className="text-sm font-black text-brand-black">
                        {formatMoney(item.amount)}{item.perDay ? ' / day' : ''}
                      </span>
                    </div>
                  )) : (
                    <div className="text-sm text-gray-500">No pricing configurations have been added yet.</div>
                  )}
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-4 border-b border-gray-50">
                    <span className="text-sm font-black">Professional Driver</span>
                    <span className="text-xs font-black uppercase tracking-widest text-brand-black">{car.professionalDriver ? 'Available' : 'Not Included'}</span>
                  </div>
                  <div className="flex items-center justify-between py-4">
                    <span className="text-sm font-black">Outside City Trips</span>
                    <span className="text-xs font-black uppercase tracking-widest text-gray-700">{car.allowOutsideCity ? 'Allowed' : 'Not Allowed'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[28px] border border-gray-100 p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <h2 className="text-xl font-black tracking-tight uppercase">Ratings & Reviews</h2>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">
                      Verified renter feedback from completed bookings
                    </p>
                  </div>
                  <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-right">
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-2">Average Rating</p>
                    <div className="flex items-center gap-2 text-amber-700">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-2xl font-black">{Number(car.averageRating || 0).toFixed(1)}</span>
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-500 mt-2">
                      {car.reviewCount || 0} reviews
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {car.reviews?.length ? car.reviews.map((review, index) => (
                    <div key={`${review.reviewerName}-${review.createdAt}-${index}`} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <p className="text-sm font-black text-brand-black">{review.reviewerName}</p>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-2">{review.createdAt || 'Recently'}</p>
                        </div>
                        <div className="flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-sm font-black text-amber-700">
                          <Star className="w-4 h-4 fill-current" />
                          {review.rating || 0}
                        </div>
                      </div>
                      <p className="text-sm leading-7 text-gray-600">
                        {review.reviewText || 'This renter left a high rating without extra comments.'}
                      </p>
                    </div>
                  )) : (
                    <div className="text-sm text-gray-500">No renter reviews have been added for this car yet.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-[28px] border border-gray-100 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-3 bg-gray-100 text-gray-700 rounded-2xl">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black tracking-tight uppercase leading-none">Documents</h2>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">Uploaded files for this car</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {car.documents?.length ? car.documents.map((doc) => (
                    <div key={doc.id} className="bg-gray-50/60 p-4 rounded-2xl border border-gray-100 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <span className="block text-xs font-black text-gray-900 tracking-tight uppercase truncate">{doc.name}</span>
                        <p className="text-[8px] font-black text-emerald-600 uppercase tracking-[0.1em] mt-1">Uploaded</p>
                      </div>
                      {doc.filePath && (
                        <button
                          onClick={() => window.open(buildPublicFileUrl(doc.filePath), '_blank')}
                          className="text-brand-black bg-brand-orange-50 hover:bg-brand-orange-100 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shrink-0"
                        >
                          Download
                        </button>
                      )}
                    </div>
                  )) : (
                    <div className="text-sm text-gray-500">No documents uploaded for this car yet.</div>
                  )}
                </div>
              </div>
            </div>
          </section>
            </>
          )}

          {activeTab === 'history' && (
            <section className="bg-white rounded-[28px] border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-3 bg-gray-100 text-gray-700 rounded-2xl">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-black tracking-tight uppercase leading-none">Flow History</h2>
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">Draft, submit, resubmit and review milestones</p>
                </div>
              </div>

              <div className="relative max-h-[720px] overflow-y-auto pr-1 pl-2">
                {!!car.history?.length && (
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-gray-300 to-gray-200" />
                )}
                {car.history?.length ? car.history.map((item, index) => (
                  <div key={`${item.eventType}-${index}`} className="relative pl-8 pb-5 last:pb-0">
                    <div className="absolute left-[6px] top-2 w-[18px] h-[18px] rounded-full bg-brand-black border-4 border-white shadow-[0_0_0_1px_rgba(209,213,219,1)]" />
                    <div className="bg-gray-50/70 p-4 rounded-[22px] border border-gray-100">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{item.createdAt || 'Recently'}</p>
                          <p className="text-sm font-black text-brand-black uppercase tracking-widest mt-2">{item.title}</p>
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-2">
                            {(item.eventType || 'EVENT').replace(/_/g, ' ')}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest ${statusColor[item.status] || 'bg-gray-100 text-gray-700'}`}>
                          {item.status ? formatStatusLabel(item.status) : 'N/A'}
                        </span>
                      </div>

                      <div className="mt-4 space-y-3">
                        {item.details ? (
                          <p className="text-sm text-gray-700 font-medium leading-6">{item.details}</p>
                        ) : (
                          <p className="text-sm text-gray-500">No extra note was added for this update.</p>
                        )}

                        {item.snapshot && (
                          <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-3">
                            <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-2">Snapshot</p>
                            <p className="text-xs text-gray-500 leading-6 whitespace-pre-wrap break-words">{item.snapshot}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-sm text-gray-500">No history available yet.</div>
                )}
              </div>
            </section>
          )}
        </div>

        {availabilityOpen && (
          <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-6">
            <div className="w-full max-w-2xl bg-white rounded-[28px] border border-gray-100 shadow-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Manage Availability</p>
                  <h3 className="text-2xl font-black text-brand-black">{car.name}</h3>
                  <p className="text-sm text-gray-500 mt-2">Choose how this vehicle should appear on the platform.</p>
                </div>
                <button onClick={() => setAvailabilityOpen(false)} className="w-10 h-10 rounded-2xl bg-gray-50 text-gray-500 hover:text-brand-black flex items-center justify-center">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid gap-4">
                {availabilityOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleStatusChange(option.value)}
                    disabled={statusSaving || car.status === option.value}
                    className={`w-full text-left rounded-3xl border p-5 transition-all disabled:opacity-50 ${
                      car.status === option.value ? 'border-brand-black bg-gray-50' : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50/50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center shrink-0">
                        <option.icon className="w-5 h-5 text-brand-black" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4">
                          <p className="text-sm font-black uppercase tracking-widest text-brand-black">{option.label}</p>
                          {car.status === option.value && (
                            <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600">Current</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-2">{option.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {statusSaving && (
                <div className="flex items-center justify-center gap-3 mt-5 text-sm font-bold text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating availability...
                </div>
              )}
            </div>
          </div>
        )}

        {pricingOpen && (
          <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-6">
            <div className="w-full max-w-3xl bg-white rounded-[28px] border border-gray-100 shadow-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Update Pricing</p>
                  <h3 className="text-2xl font-black text-brand-black">{car.name}</h3>
                  <p className="text-sm text-gray-500 mt-2">Adjust your live pricing tiers without editing the whole vehicle profile.</p>
                </div>
                <button onClick={() => setPricingOpen(false)} className="w-10 h-10 rounded-2xl bg-gray-50 text-gray-500 hover:text-brand-black flex items-center justify-center">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">Outside City Price</label>
                  <input
                    type="number"
                    min="1"
                    value={pricingForm.outsideCityPrice}
                    onChange={(e) => setPricingForm((current) => ({ ...current, outsideCityPrice: e.target.value }))}
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-brand-black outline-none focus:border-brand-black"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PRICING_TIER_OPTIONS.map((option) => {
                  const currentValue = pricingForm.pricingConfigurations.find((item) => item.pricingType === option.pricingType)?.amount ?? '';
                  return (
                    <div key={option.pricingType} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5">
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <div>
                          <p className="text-sm font-black text-brand-black">{option.label}</p>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">{option.rangeLabel}</p>
                        </div>
                      </div>
                      <input
                        type="number"
                        min="1"
                        value={currentValue}
                        onChange={(e) => handlePricingValueChange(option.pricingType, e.target.value)}
                        className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-brand-black outline-none focus:border-brand-black"
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setPricingOpen(false)}
                  className="px-5 py-3 rounded-2xl border border-gray-200 text-xs font-black uppercase tracking-widest text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePricingSave}
                  disabled={pricingSaving}
                  className="px-5 py-3 rounded-2xl bg-brand-black text-white text-xs font-black uppercase tracking-widest hover:bg-gray-800 disabled:opacity-60"
                >
                  {pricingSaving ? 'Saving...' : 'Save Pricing'}
                </button>
              </div>
            </div>
          </div>
        )}

        {imagesOpen && (
          <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-6">
            <div className="w-full max-w-4xl bg-white rounded-[28px] border border-gray-100 shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Manage Images</p>
                  <h3 className="text-2xl font-black text-brand-black">{car.name}</h3>
                  <p className="text-sm text-gray-500 mt-2">Replace your primary image, upload new gallery photos, or remove old ones.</p>
                </div>
                <button onClick={() => setImagesOpen(false)} className="w-10 h-10 rounded-2xl bg-gray-50 text-gray-500 hover:text-brand-black flex items-center justify-center">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-6">
                <div className="rounded-3xl border border-gray-100 bg-gray-50/60 p-5">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">Primary Image</p>
                  <div className="rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3] mb-4">
                    <img
                      src={buildImageUrl(car.primaryImageUrl)}
                      alt={`${car.name} primary`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-brand-black text-white text-xs font-black uppercase tracking-widest cursor-pointer hover:bg-gray-800 transition-all">
                    <Upload className="w-4 h-4" />
                    {imageUploading ? 'Uploading...' : 'Replace Primary'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={imageUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handlePrimaryImageUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>

                <div className="rounded-3xl border border-gray-100 bg-gray-50/60 p-5">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Gallery Images</p>
                    <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-gray-200 bg-white text-xs font-black uppercase tracking-widest cursor-pointer hover:bg-gray-50 transition-all">
                      <Upload className="w-4 h-4" />
                      {imageUploading ? 'Uploading...' : 'Add Images'}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        disabled={imageUploading}
                        onChange={(e) => {
                          handleGalleryImagesUpload(e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>

                  {car.images?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {car.images.map((image, index) => (
                        <div key={image.id || index} className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                          <div className="aspect-[4/3] bg-gray-100">
                            <img
                              src={buildImageUrl(image.imageUrl)}
                              alt={`${car.name} gallery ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="p-3">
                            <button
                              type="button"
                              onClick={() => handleDeleteGalleryImage(image.id)}
                              disabled={imageDeletingId === image.id}
                              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 text-[10px] font-black uppercase tracking-widest disabled:opacity-60"
                            >
                              <Trash2 className="w-4 h-4" />
                              {imageDeletingId === image.id ? 'Removing...' : 'Remove'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                      No gallery images uploaded yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setImagesOpen(false)}
                  className="px-5 py-3 rounded-2xl border border-gray-200 text-xs font-black uppercase tracking-widest text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default VehicleDetailsOwner;
